import React from 'react';
import { Clock, Users, ChefHat, Tag } from 'lucide-react';
import RecipeSuggestionsWidget from '../widgets/RecipeSuggestionsWidget';
import FarmSourcesWidget from '../widgets/FarmSourcesWidget';
import SeasonalProduceWidget from '../widgets/SeasonalProduceWidget';
import RelatedContentWidget from '../widgets/RelatedContentWidget';

interface Recipe {
  id: string;
  title?: string;
  name?: string;
  description: string;
  ingredients: any[];
  primaryProduce: string[];
  prepTime: number | string;
  cookTime: number | string;
  servings: number;
  difficulty: 'easy' | 'medium' | 'hard';
  cuisine?: string;
  dietaryTags: string[];
  instructions: string[];
  nutritionInfo?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
}

interface RelatedItem {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  url: string;
  type: 'recipe' | 'blog' | 'produce' | 'farm' | 'seasonal';
  image?: string;
  meta?: string;
}

interface RecipeSidebarProps {
  recipe: Recipe;
  relatedRecipes?: RelatedItem[];
  showFarmSources?: boolean;
  showSeasonalProduce?: boolean;
}

const RecipeSidebar: React.FC<RecipeSidebarProps> = ({
  recipe,
  relatedRecipes = [],
  showFarmSources = true,
  showSeasonalProduce = true,
}) => {
  const recipeName = recipe.title || recipe.name || 'Recipe';

  const parseTime = (time: number | string): number => {
    if (typeof time === 'number') return time;
    if (typeof time === 'string') {
      const match = time.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    }
    return 0;
  };

  const prepTime = parseTime(recipe.prepTime);
  const cookTime = parseTime(recipe.cookTime);
  const totalTime = prepTime + cookTime;

  const getDifficultyColor = (difficulty: string): string => {
    switch (difficulty) {
      case 'easy':
        return '#10b981';
      case 'medium':
        return '#f59e0b';
      case 'hard':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getDifficultyLabel = (difficulty: string): string => {
    return difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
  };

  const styles = {
    sidebar: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    quickInfoWidget: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    quickInfoTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
    },
    quickInfoIcon: {
      marginRight: '12px',
      color: '#2D5A27',
    },
    infoGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
      marginBottom: '20px',
    },
    infoItem: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      padding: '12px',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
    },
    infoIcon: {
      marginBottom: '8px',
      color: '#2D5A27',
    },
    infoLabel: {
      fontSize: '11px',
      color: '#666',
      textTransform: 'uppercase' as const,
      fontWeight: '600',
      marginBottom: '4px',
    },
    infoValue: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a1a',
    },
    difficultyBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '8px 16px',
      borderRadius: '20px',
      fontWeight: '600',
      fontSize: '14px',
      color: '#fff',
      marginBottom: '20px',
    },
    tagsSection: {
      paddingTop: '16px',
      borderTop: '1px solid #e5e5e5',
    },
    tagsTitle: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase' as const,
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
    },
    tagsGrid: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
    },
    tag: {
      padding: '6px 12px',
      backgroundColor: '#f0f7ef',
      color: '#2D5A27',
      borderRadius: '16px',
      fontSize: '12px',
      fontWeight: '600',
      border: '1px solid #d4e7d2',
    },
    nutritionSection: {
      paddingTop: '16px',
      borderTop: '1px solid #e5e5e5',
    },
    nutritionTitle: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#666',
      textTransform: 'uppercase' as const,
      marginBottom: '12px',
    },
    nutritionGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '8px',
    },
    nutritionItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px',
      backgroundColor: '#f9f9f9',
      borderRadius: '4px',
      fontSize: '13px',
    },
    nutritionLabel: {
      color: '#666',
    },
    nutritionValue: {
      fontWeight: '600',
      color: '#1a1a1a',
    },
  };

  return (
    <div style={styles.sidebar}>
      {/* Quick Info Widget */}
      <div style={styles.quickInfoWidget}>
        <h3 style={styles.quickInfoTitle}>
          <ChefHat style={styles.quickInfoIcon} size={24} />
          Recipe Info
        </h3>

        <div style={styles.infoGrid}>
          <div style={styles.infoItem}>
            <Clock style={styles.infoIcon} size={24} />
            <div style={styles.infoLabel}>Prep Time</div>
            <div style={styles.infoValue}>{prepTime} min</div>
          </div>

          <div style={styles.infoItem}>
            <Clock style={styles.infoIcon} size={24} />
            <div style={styles.infoLabel}>Cook Time</div>
            <div style={styles.infoValue}>{cookTime} min</div>
          </div>

          <div style={styles.infoItem}>
            <Clock style={styles.infoIcon} size={24} />
            <div style={styles.infoLabel}>Total Time</div>
            <div style={styles.infoValue}>{totalTime} min</div>
          </div>

          <div style={styles.infoItem}>
            <Users style={styles.infoIcon} size={24} />
            <div style={styles.infoLabel}>Servings</div>
            <div style={styles.infoValue}>{recipe.servings}</div>
          </div>
        </div>

        <div
          style={{
            ...styles.difficultyBadge,
            backgroundColor: getDifficultyColor(recipe.difficulty),
          }}
        >
          <ChefHat size={16} style={{ marginRight: '8px' }} />
          {getDifficultyLabel(recipe.difficulty)} Recipe
        </div>

        {recipe.dietaryTags && recipe.dietaryTags.length > 0 && (
          <div style={styles.tagsSection}>
            <div style={styles.tagsTitle}>
              <Tag size={14} style={{ marginRight: '6px' }} />
              Dietary Tags
            </div>
            <div style={styles.tagsGrid}>
              {recipe.dietaryTags.map((tag, index) => (
                <span key={index} style={styles.tag}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {recipe.nutritionInfo && (
          <div style={styles.nutritionSection}>
            <div style={styles.nutritionTitle}>Nutrition (Per Serving)</div>
            <div style={styles.nutritionGrid}>
              <div style={styles.nutritionItem}>
                <span style={styles.nutritionLabel}>Calories</span>
                <span style={styles.nutritionValue}>
                  {recipe.nutritionInfo.calories}
                </span>
              </div>
              <div style={styles.nutritionItem}>
                <span style={styles.nutritionLabel}>Protein</span>
                <span style={styles.nutritionValue}>
                  {recipe.nutritionInfo.protein}g
                </span>
              </div>
              <div style={styles.nutritionItem}>
                <span style={styles.nutritionLabel}>Carbs</span>
                <span style={styles.nutritionValue}>
                  {recipe.nutritionInfo.carbs}g
                </span>
              </div>
              <div style={styles.nutritionItem}>
                <span style={styles.nutritionLabel}>Fat</span>
                <span style={styles.nutritionValue}>
                  {recipe.nutritionInfo.fat}g
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Farm Sources Widget - KEY for "Basil Example" vice versa */}
      {showFarmSources && recipe.primaryProduce && recipe.primaryProduce.length > 0 && (
        <FarmSourcesWidget
          ingredients={recipe.primaryProduce}
          maxFarms={5}
          title="Where to Buy Ingredients"
          showDistance={true}
        />
      )}

      {/* Related Recipes */}
      {relatedRecipes && relatedRecipes.length > 0 && (
        <RelatedContentWidget
          items={relatedRecipes}
          title="Similar Recipes"
          contentType="recipe"
          maxItems={5}
          layout="list"
        />
      )}

      {/* Seasonal Produce Widget */}
      {showSeasonalProduce && (
        <SeasonalProduceWidget
          maxItems={6}
        />
      )}
    </div>
  );
};

export default RecipeSidebar;
